import { useState, useEffect } from "react";
import { Alert, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import SntDefaultUserIcon from "@icons/SntDefaultUserIcon";
import { apiClient } from "@/apis/apiClient";
import { loginSuccess } from "@/redux/actions";
import styled, { createGlobalStyle } from "styled-components";
import GlobalUtils from "@/utils/GlobalUtils";
import SntDialog from "@/components/SntDialog/SntDialog";

const AccountItem = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  margin-bottom: 8px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: white;

  &:hover:not(.current) {
    background: #f8f9fa;
    border-color: #dee2e6;
  }

  &.current {
    background: #e3f2fd;
    border-color: #2196f3;
  }

  &.current.disabled {
    cursor: default;
  }

  &.current:not(.disabled) {
    cursor: pointer;
  }

  .avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    margin-right: 12px;
    flex-shrink: 0;
  }

  .account-info {
    flex: 1;

    .username {
      font-weight: 500;
      color: #333;
      margin-bottom: 2px;
    }

    .account-type {
      color: #666;
      font-size: 0.875rem;
    }
  }
`;

const AccountSwitchDialogStyles = createGlobalStyle`
  .account-switch-dialog-centered .modal-dialog {
    display: flex;
    align-items: center;
    min-height: calc(100vh - 1rem);
    margin: 0.5rem auto;
  }
  
  .account-switch-dialog-centered .modal-content {
    margin: auto;
  }
`;

const AccountSwitchDialog = ({
  show,
  onHide,
  currentUser,
  language,
  openedFrom = "header",
}) => {
  const [accounts, setAccounts] = useState([]);
  const [error, setError] = useState(null);
  const [switching, setSwitching] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (show && currentUser?.availableAccounts) {
      console.log(
        "Setting accounts from login info:",
        currentUser.availableAccounts
      );
      setAccounts(currentUser.availableAccounts);
    } else if (show) {
      console.log("No available accounts in login info");
      setAccounts([]);
    }
  }, [show, currentUser?.availableAccounts]);

  const switchAccount = async (accountId) => {
    setSwitching(true);
    setError(null);
    try {
      const response = await apiClient.put(
        `/rest/authentication/accounts/switch/${accountId}`
      );

      // Update authentication state with new login info
      dispatch(loginSuccess(response.data));

      // Close dialog and redirect to home page to reflect new user context
      onHide();
      window.location.href = "/";
    } catch (err) {
      setError(
        err.response?.data?.message || language.error_switching_account_key
      );
    } finally {
      setSwitching(false);
    }
  };

  const renderAccountItem = (account) => {
    const isCurrentUser = account.currentUser;
    const shouldDisableCurrentUser = openedFrom === "header";

    return (
      <AccountItem
        key={account.id}
        className={`${isCurrentUser ? "current" : ""} ${
          isCurrentUser && shouldDisableCurrentUser ? "disabled" : ""
        }`}
        onClick={() => {
          if (isCurrentUser && !shouldDisableCurrentUser) {
            // If current user clicked from complete screen, just close dialog
            onHide();
          } else if (
            !(isCurrentUser && shouldDisableCurrentUser) &&
            !switching
          ) {
            switchAccount(account.id);
          }
        }}
      >
        {account.image ? (
          <img
            src={GlobalUtils.getFile(
              "user",
              account.id,
              "avatar",
              account.image
            )}
            alt={account.fullName}
            className="avatar"
          />
        ) : (
          <SntDefaultUserIcon size={40} className="avatar" />
        )}

        <div className="account-info">
          <div className="username">
            {account.fullName || account.username}
            {isCurrentUser && (
              <span
                style={{
                  marginLeft: "8px",
                  fontSize: "0.75rem",
                  color: "#666",
                  fontWeight: "500",
                }}
              >
                (Current)
              </span>
            )}
          </div>
          <div className="account-type">{account.roleName}</div>
        </div>

        {switching && <Spinner animation="border" size="sm" />}
      </AccountItem>
    );
  };

  return (
    <>
      <AccountSwitchDialogStyles />
      <SntDialog
        isShow={show}
        onHide={onHide}
        onClose={onHide}
        title={language.select_account_key}
        size="sm"
        isShowSaveButton={false}
        isShowCloseButton={openedFrom === "header"}
        customModalClassname="account-switch-dialog-centered"
      >
        {() => (
          <>
            {error && (
              <Alert variant="danger" className="mb-3">
                {error}
              </Alert>
            )}

            {accounts.length === 0 ? (
              <Alert variant="info">
                {language.no_additional_accounts_key}
              </Alert>
            ) : (
              <div>{accounts.map((account) => renderAccountItem(account))}</div>
            )}
          </>
        )}
      </SntDialog>
    </>
  );
};

export default AccountSwitchDialog;
